var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../../services/api-client';
let JvAdminDashboard = class JvAdminDashboard extends LitElement {
    constructor() {
        super(...arguments);
        this._stats = null;
        this._audit = [];
        this._loadError = '';
        this._loading = true;
    }
    static { this.styles = css `
    :host { display: block; }
    .stat-card {
      background: var(--jv-surface);
      border: 1px solid var(--jv-border);
      border-radius: 16px;
      padding: 20px;
    }
    .stat-value { font-size: 28px; font-weight: 800; color: var(--jv-ink); line-height: 1.1; }
    .stat-label { font-size: 13px; color: var(--jv-ink-2); margin-top: 4px; }
    .nav-card {
      background: var(--jv-surface);
      border: 1px solid var(--jv-border);
      border-radius: 16px;
      padding: 16px 20px;
      display: flex;
      align-items: center;
      gap: 14px;
      cursor: pointer;
      transition: all .15s;
    }
    .nav-card:hover { box-shadow: 0 4px 12px rgba(58,53,48,.08); border-color: var(--jv-primary); }
    .icon-box {
      width: 44px; height: 44px; border-radius: 12px;
      display: grid; place-items: center; flex-shrink: 0;
    }
  `; }
    connectedCallback() {
        super.connectedCallback();
        void this._load();
    }
    async _load() {
        this._loading = true;
        this._loadError = '';
        try {
            const [sites, agents, audit] = await Promise.all([
                api.get('/api/sites'),
                api.get('/api/agents'),
                api.get('/api/admin/tenant/audit?take=10'),
            ]);
            this._audit = audit;
            this._stats = {
                totalCameras: 0,
                onlineCameras: 0,
                totalSites: sites.length,
                totalParents: 0,
                totalAgents: agents.length,
                activeViewers: agents.filter(a => a.status === 'online').length,
                quotaUsedMinutes: 0,
                quotaTotalMinutes: 300,
            };
        }
        catch (err) {
            this._loadError = err?.message ?? 'Unable to load dashboard data. Try again.';
        }
        finally {
            this._loading = false;
        }
    }
    _nav(route) {
        this.dispatchEvent(new CustomEvent('admin-nav', {
            detail: { route },
            bubbles: true,
            composed: true,
        }));
    }
    render() {
        const p = this.profile;
        const s = this._stats;
        return html `
      <div class="max-w-content mx-auto px-5 sm:px-8 py-7">
        <div class="mb-6">
          <h1 class="text-2xl font-extrabold tracking-tight text-jv-ink">${p.displayName.split(' ')[0]}'s school</h1>
          <p class="text-sm text-jv-ink-2">Manage your cameras, parents, and settings</p>
        </div>

        ${this._loading ? html `<p class="text-jv-ink-2 text-sm" role="status">Loading dashboard…</p>` : this._loadError ? html `
          <div class="jv-card p-6" role="alert"><p class="text-jv-danger text-sm">${this._loadError}</p><button @click=${this._load} class="jv-btn-primary text-xs mt-4">Retry</button></div>
        ` : html `
          <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
            <div class="stat-card"><div class="stat-value">${s?.totalSites ?? '—'}</div><div class="stat-label">Sites</div></div>
            <div class="stat-card"><div class="stat-value">${s?.totalCameras ?? '—'}</div><div class="stat-label">Cameras</div></div>
            <div class="stat-card"><div class="stat-value">${s?.totalAgents ?? '—'}</div><div class="stat-label">Edge Agents</div></div>
            <div class="stat-card"><div class="stat-value">${s?.totalParents ?? '—'}</div><div class="stat-label">Parents</div></div>
            <div class="stat-card"><div class="stat-value">${s?.activeViewers ?? '—'}</div><div class="stat-label">Active Now</div></div>
          </div>

          <h2 class="text-sm font-semibold uppercase tracking-wider text-jv-ink-2 mb-3">Management</h2>
          <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
            <div class="nav-card" @click=${() => this._nav('cameras')}>
              <span class="icon-box bg-jv-primary-050 text-jv-primary"><svg viewBox="0 0 24 24" class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7S2 12 2 12z"/><circle cx="12" cy="12" r="3"/></svg></span>
              <div><div class="font-bold text-sm text-jv-ink">Cameras</div><div class="text-xs text-jv-ink-2">Add, configure, assign</div></div>
            </div>
            <div class="nav-card" @click=${() => this._nav('sites')}>
              <span class="icon-box bg-jv-primary-050 text-jv-primary"><svg viewBox="0 0 24 24" class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg></span>
              <div><div class="font-bold text-sm text-jv-ink">Sites</div><div class="text-xs text-jv-ink-2">Buildings & locations</div></div>
            </div>
            <div class="nav-card" @click=${() => this._nav('agents')}>
              <span class="icon-box bg-jv-primary-050 text-jv-primary"><svg viewBox="0 0 24 24" class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg></span>
              <div><div class="font-bold text-sm text-jv-ink">Edge Agents</div><div class="text-xs text-jv-ink-2">Enroll & monitor</div></div>
            </div>
            <div class="nav-card" @click=${() => this._nav('parents')}>
              <span class="icon-box bg-jv-primary-050 text-jv-primary"><svg viewBox="0 0 24 24" class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg></span>
              <div><div class="font-bold text-sm text-jv-ink">Parents & Access</div><div class="text-xs text-jv-ink-2">Invite, assign cameras</div></div>
            </div>
            <div class="nav-card" @click=${() => this._nav('settings')}>
              <span class="icon-box bg-jv-accent-050 text-jv-accent-600"><svg viewBox="0 0 24 24" class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg></span>
              <div><div class="font-bold text-sm text-jv-ink">Settings</div><div class="text-xs text-jv-ink-2">Hours, quotas, billing</div></div>
            </div>
          </div>

          ${this._audit.length > 0 ? html `
            <h2 class="text-sm font-semibold uppercase tracking-wider text-jv-ink-2 mb-3">Recent Activity</h2>
            <div class="jv-card">
              <div class="divide-y divide-jv-border">
                ${this._audit.slice(0, 8).map(e => html `
                  <div class="flex items-center gap-3 px-5 py-3">
                    <span class="text-xs font-mono text-jv-ink-2 w-20 shrink-0">${new Date(e.timestamp).toLocaleTimeString()}</span>
                    <span class="text-sm text-jv-ink truncate">${e.description}</span>
                  </div>
                `)}
              </div>
            </div>
          ` : ''}
        `}
      </div>
    `;
    }
};
__decorate([
    property({ type: Object })
], JvAdminDashboard.prototype, "profile", void 0);
__decorate([
    state()
], JvAdminDashboard.prototype, "_stats", void 0);
__decorate([
    state()
], JvAdminDashboard.prototype, "_audit", void 0);
__decorate([
    state()
], JvAdminDashboard.prototype, "_loadError", void 0);
__decorate([
    state()
], JvAdminDashboard.prototype, "_loading", void 0);
JvAdminDashboard = __decorate([
    customElement('jv-admin-dashboard')
], JvAdminDashboard);
export { JvAdminDashboard };
